import { Component, computed, input, ChangeDetectionStrategy } from '@angular/core';
import { BaseChartComponent, ChartOptions } from '@galaxy/shared/utils/charts';
import DOMPurify from 'dompurify';
import { ChartUIComponentInterface } from '../../../../../../../../core/src/lib/interface/conversation.interface';

/**
 * Parses a chart UIComponent's apex config into ChartOptions, stripping HTML from
 * every string field. The config's free-text fields (series names, category
 * labels) originate from an LLM tool call, and ApexCharts inserts legend text and
 * tooltip series names/x-labels via innerHTML without escaping — so all strings
 * are sanitized here at the sink before they can reach the chart (DALE-1453).
 * Returns null (render nothing) for missing, malformed, or non-object input.
 */
export function parseChartOptions(apexConfigJson: string | undefined): ChartOptions | null {
  if (!apexConfigJson) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(apexConfigJson);
  } catch {
    console.warn('message-chart: dropping unparseable apex config');
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    console.warn('message-chart: dropping non-object apex config');
    return null;
  }
  const sanitized = sanitizeStrings(parsed) as Record<string, unknown>;
  decodeSvgTextFields(sanitized);
  enforceInboxChartBehavior(sanitized);
  return sanitized as ChartOptions;
}

/**
 * Charts render inside a scrollable feed: ApexCharts enables mouse-wheel zoom
 * by default, so scrolling the chat with the cursor over a chart zooms its
 * x-axis instead (reading as lost data points), and the toolbar invites
 * dashboard interactions the feed can't support. Enforced at the sink so every
 * stored config — past or future, whatever the producer sends — behaves as a
 * static chat chart.
 */
function enforceInboxChartBehavior(options: Record<string, unknown>): void {
  const chart = ((options['chart'] as Record<string, unknown>) ?? {});
  chart['zoom'] = { enabled: false };
  chart['toolbar'] = { show: false };
  options['chart'] = chart;
}

/**
 * Recursively strips HTML from every string value; other values pass through.
 * Prototype-pollution keys are dropped entirely: JSON.parse creates `__proto__`
 * as an own property and Object.fromEntries would preserve it, letting a hostile
 * config reach ApexCharts' recursive option merge.
 */
const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype'];

function sanitizeStrings(value: unknown): unknown {
  if (typeof value === 'string') {
    return DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeStrings);
  }
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([k]) => !FORBIDDEN_KEYS.includes(k))
        .map(([k, v]) => [k, sanitizeStrings(v)]),
    );
  }
  return value;
}

/**
 * DOMPurify entity-encodes bare `<`/`&` (e.g. "Deals < $1M" → "Deals &lt; $1M").
 * That's exactly right for the innerHTML sinks (legend, tooltip — the browser
 * decodes entities back for display), but the chart and axis TITLES render as SVG
 * text nodes (textContent), which display entities literally. Decode entities on
 * only those title fields so legit `<`/`&` display correctly. This cannot
 * reintroduce XSS: SVG text nodes never parse markup, and these fields are never
 * innerHTML'd by ApexCharts (verified against its source: titles go through
 * drawText → tspan). Never apply this decode to series names or categories.
 */
function decodeSvgTextFields(options: Record<string, unknown>): void {
  // ApexCharts types yaxis as ApexYAxis | ApexYAxis[]; handle both shapes so
  // multi-axis titles don't silently skip the decode.
  const yaxisValue = options['yaxis'];
  const yaxisHolders = Array.isArray(yaxisValue)
    ? (yaxisValue as Record<string, unknown>[]).map((y) => y?.['title'])
    : [(yaxisValue as Record<string, unknown>)?.['title']];

  for (const holder of [
    options['title'],
    (options['xaxis'] as Record<string, unknown>)?.['title'],
    ...yaxisHolders,
  ]) {
    if (typeof holder === 'object' && holder !== null) {
      const t = holder as Record<string, unknown>;
      if (typeof t['text'] === 'string') {
        t['text'] = decodeBasicEntities(t['text']);
      }
    }
  }
}

function decodeBasicEntities(s: string): string {
  return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

@Component({
  selector: 'inbox-message-chart',
  imports: [BaseChartComponent],
  templateUrl: './message-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './message-chart.component.scss',
})
export class MessageChartComponent {
  chartComponent = input.required<ChartUIComponentInterface>();

  // String-valued intermediate: the conversation store recreates every message
  // object per emission, but Object.is on the config STRING short-circuits the
  // downstream computed — the chart only rebuilds when the config itself changes,
  // not on every receipt/status/streaming update.
  private readonly apexConfigJson = computed(() => this.chartComponent().chart?.apexConfigJson);

  readonly options = computed(() => parseChartOptions(this.apexConfigJson()));

  /** Chart title from the parsed config — the accessible caption for the chart region. */
  readonly chartTitle = computed(() => {
    const options = this.options() as { title?: { text?: string } } | null;
    return options?.title?.text ?? '';
  });
}
